import React, { useState } from 'react';
import { Alert, Platform, ScrollView, Share, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import SvgIcon from '../../../../components/Icon/Icon';
import IconButton from '../../../../components/IconButton';
import BottomSheet from '../../../../components/BottomSheet';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import api from '../../../../utils/api';
import { useToast } from 'react-native-toast-notifications';
import StaffRolePicker from './StaffRolePicker';


interface AdminUserDetailModalProps {
  visible: boolean;
  user: {
    uuid: string;
    name: string;
    email: string;
    phone_number: string;
    is_registered: boolean;
    roles: Array<{ name: string; display_name: string }>;
  };
  onRequestClose: () => void;
  onUserUpdated: () => void;
  onUserDeleted: () => void;
}


/** Detalle de alguien del equipo: sus roles en la comunidad, invitación y baja. */
export default function AdminUserDetailModal({
  visible,
  user,
  onRequestClose,
  onUserUpdated,
  onUserDeleted,
}: AdminUserDetailModalProps) {
  const { communityId } = useActiveAdminCommunity()
  const toast = useToast()

  const initialRoles = user.roles?.map(role => role.name) ?? [];
  const [roles, setRoles] = useState<string[]>(initialRoles);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const rolesChanged = roles.length !== initialRoles.length || roles.some(role => !initialRoles.includes(role));

  const saveRoles = async () => {
    setIsUpdating(true);
    try {
      await api.patch(`/communities/${communityId}/staff/${user.uuid}/roles`, { roles });
      toast.show("Roles actualizados", { type: "success" });
      onUserUpdated();
    } catch (error: any) {
      toast.show(error.response?.data?.message || "Error al actualizar roles", { type: "warning" });
    } finally {
      setIsUpdating(false);
    }
  };

  // Liga para que cree su cuenta (solo si aún no se registra).
  const shareInvitation = async () => {
    setIsInviting(true);
    try {
      const { data } = await api.post(`/staff/${user.uuid}/invitation`);
      await Share.share({ message: data.message ?? data.url });
    } catch (error: any) {
      toast.show(error.response?.data?.message || "No se pudo generar la invitación", { type: "warning" });
    } finally {
      setIsInviting(false);
    }
  };

  const deleteStaffUser = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/communities/${communityId}/staff/${user.uuid}`);
      toast.show("Quitado del equipo", { type: "success" });
      onUserDeleted();
    } catch (error: any) {
      toast.show(error.response?.data?.message || "Error al quitar del equipo", { type: "warning" });
    } finally {
      setIsDeleting(false);
    }
  }

  const handleDeleteUser = () => {
    const message = `¿Quitar a ${user.name} del equipo? Perderá el acceso a esta comunidad.`;

    if (Platform.OS === 'web') {
      if (window.confirm(message)) deleteStaffUser();
      return;
    }

    Alert.alert("Quitar del equipo", message, [
      { text: "Cancelar", style: "cancel" },
      { text: "Quitar", style: "destructive", onPress: deleteStaffUser },
    ]);
  };

  return (
    <BottomSheet
      visible={visible}
      onRequestClose={onRequestClose}
      height={700}
    >
      <View style={tw`flex-1`}>
        {/* Header */}
        <View style={tw`row justify-between items-center px-5 pt-5 pb-4 border-b border-gray-200`}>
          <View style={tw`flex-1`}>
            <View style={tw`row items-center`}>
              <Text style={tw`text-lg font-600 text-secondary`}>{user.name}</Text>
              {user.is_registered && (
                <SvgIcon name='verified' size={18} color={tw.color('primary')} style={tw`ml-2`} />
              )}
            </View>
            <Text style={tw`text-gray-500 text-sm`}>{user.email}</Text>
          </View>

          <TouchableOpacity onPress={onRequestClose}>
            <Icon name='close' style={tw`text-red`} size={26} />
          </TouchableOpacity>
        </View>

        <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-5 py-4`}>
          <Text style={tw`text-gray-700 font-600 text-lg mb-1`}>Roles</Text>
          <StaffRolePicker value={roles} onChange={setRoles} disabled={isUpdating} />

          {rolesChanged && (
            <IconButton
              icon='check'
              title={isUpdating ? "Guardando..." : "Guardar roles"}
              disabled={isUpdating || roles.length === 0}
              style={tw`w-full py-3 mt-6`}
              onPress={saveRoles}
            />
          )}

          {!user.is_registered && (
            <TouchableOpacity
              style={tw`row items-center justify-center py-3 border border-primary rounded-full mt-6`}
              onPress={shareInvitation}
              disabled={isInviting}
            >
              <Text style={tw`text-primary font-500`}>
                {isInviting ? "Generando..." : "Compartir invitación para crear su cuenta"}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={tw`row items-center justify-center py-3 border border-red rounded-full mt-4`}
            onPress={handleDeleteUser}
            disabled={isDeleting}
          >
            <Text style={tw`text-red font-500 mr-2`}>
              {isDeleting ? "Quitando..." : "Quitar del equipo"}
            </Text>
            <Icon name='close' size={18} color={tw.color('red')} />
          </TouchableOpacity>
        </ScrollView>
      </View>
    </BottomSheet>
  );
}
